/**
 * The learner's recorder: the KT1 capture session (AudioWorklet at the device
 * rate) → 16 kHz mono PCM WAV ≤ 30 s (`buildKt1Wav`). Recording never leaves
 * the device here; the screen sends it only when the learner presses submit.
 *
 * `toRecording` holds the decisions (permission denied, interruption, empty
 * audio) so they are unit-tested without a microphone.
 */

import { type CaptureOutcome, CaptureSession, MAX_CAPTURE_SECONDS } from './capture'
import { buildKt1Wav } from './kt1-wav'
import { readWavSamples } from './wav'

export const MAX_RECORDING_SECONDS = MAX_CAPTURE_SECONDS
/** Shorter than this is treated as "nothing recorded" (a tap, not a reading). */
export const MIN_RECORDING_SECONDS = 0.5

export interface Recording {
  /** 16 kHz mono 16-bit PCM WAV, accepted by `assess-reading`. */
  readonly wav: ArrayBuffer
  readonly seconds: number
  /** Audio beyond 30 s was cut. */
  readonly truncated: boolean
}

export type RecordingProblem =
  'permission_denied' | 'no_microphone' | 'interrupted' | 'empty' | 'failed'

export type RecordingResult =
  | { readonly ok: true; readonly recording: Recording }
  | { readonly ok: false; readonly problem: RecordingProblem }

export interface RecorderHandle {
  /** Stop and keep the recording. */
  stop(): void
  /** Stop and discard. */
  cancel(): void
}

/** Starts recording; must be called synchronously from a tap (iOS). */
export type Recorder = (onFinished: (result: RecordingResult) => void) => RecorderHandle

export async function toRecording(outcome: CaptureOutcome): Promise<RecordingResult> {
  const error = outcome.report.error ?? ''
  if (/NotAllowedError|SecurityError|PermissionDenied/i.test(error))
    return { ok: false, problem: 'permission_denied' }
  if (/NotFoundError|OverconstrainedError|NotReadableError/i.test(error))
    return { ok: false, problem: 'no_microphone' }
  if (outcome.report.stopReason === 'track-ended')
    return { ok: false, problem: 'interrupted' }
  if (error !== '' || outcome.audio === null) return { ok: false, problem: 'failed' }
  try {
    const { samples, info } = readWavSamples(await outcome.audio.arrayBuffer())
    if (samples.length < MIN_RECORDING_SECONDS * info.sampleRate)
      return { ok: false, problem: 'empty' }
    const built = buildKt1Wav(samples, info.sampleRate)
    return {
      ok: true,
      recording: {
        wav: built.wav,
        seconds: built.info.durationSeconds,
        truncated: built.truncated,
      },
    }
  } catch {
    return { ok: false, problem: 'failed' }
  }
}

export const browserRecorder: Recorder = (onFinished) => {
  let cancelled = false
  const session = new CaptureSession('worklet-native', false, (outcome) => {
    if (cancelled) return
    void toRecording(outcome).then(onFinished)
  })
  // Synchronous inside the tap handler: required for iOS audio unlock.
  session.start()
  return {
    stop: () => void session.stop('user'),
    cancel: () => {
      cancelled = true
      void session.stop('user')
    },
  }
}
